import type { EditGraph, RenderVariant, ResolvedEdit, SourcePack, PreviewSession } from "@desk/core";
import {
  buildHybridTimeline,
  hybridEditTickToVideoTimeSec,
  type HybridTimeline,
} from "./lib/hybrid-timeline.js";
import { seekVideoWithGate } from "./lib/video-seek.js";
import { effectiveSound, isAudibleSound } from "@desk/core";
import { WARM_AUDIO_ASSET_ID } from "../../content/ids.js";
import { SfxScheduler } from "./sfx-scheduler.js";

export type VideoElementFactory = () => HTMLVideoElement;
export type AudioElementFactory = () => HTMLAudioElement;

/**
 * Maps the resolved sound choice to the matching `kind:"audio"` asset id so the
 * hybrid preview plays the SELECTED bed — Tense → `music-sector`, Upbeat →
 * `music-pulse`, Warm → the airy bed — instead of a warm/ominous binary.
 *
 * - No sound op (fresh pack) → the warm/airy bed default.
 * - Explicit silence (Clean) → "" so the stem stays silent.
 * - Any audible bed → its own assetId when it resolves to a real audio asset in
 *   the pack; otherwise falls back to the warm bed.
 */
export function resolveActiveStemAssetId(resolved: ResolvedEdit, pack: SourcePack): string {
  const sound = effectiveSound(resolved);
  // Fresh pack / undo-to-empty: default to the warm airy bed.
  if (!sound) return WARM_AUDIO_ASSET_ID;
  // Clean treatment (silence / muted): no bed rides the audio element.
  if (!isAudibleSound(sound)) return "";
  // Play the chosen bed when it maps to a real audio asset in the pack.
  const asset = pack.assets.find((a) => a.id === sound.assetId && a.kind === "audio");
  return asset ? sound.assetId : WARM_AUDIO_ASSET_ID;
}

function stemSrc(assetId: string, pack: SourcePack): string {
  const asset = pack.assets.find((a) => a.id === assetId && a.kind === "audio");
  return asset ? `/${asset.path}` : "";
}

/** Injectable monotonic clock (ms). Test seam replaces `performance.now`. */
export type PlaybackClock = () => number;

export class HybridPreviewSession implements PreviewSession {
  private tick = 0;
  /** Monotonically increasing epoch; incremented at the start of each seek.
   *  Older awaiting seeks compare their captured epoch against this value and
   *  resolve as "superseded" if it has changed — preventing stale-frame commits. */
  private _seekEpoch = 0;
  private timeline: HybridTimeline;
  private playing = false;
  private disposed = false;
  private raf = 0;
  private lastWall = 0;
  private playResolve: (() => void) | null = null;
  /** Bound `timeupdate` handler for the native continuous-playback path; removed
   *  on pause/dispose so a stopped preview never keeps firing UI ticks. */
  private timeUpdateHandler: (() => void) | null = null;
  /** Bound `ended` handler for the native path. */
  private endedHandler: (() => void) | null = null;
  /** Index (into `timeline.segments`) of the KEPT beat the native continuous
   *  playback is currently inside. The native loop advances this at each beat
   *  boundary and seeks the `<video>` when the next kept beat's source range is
   *  not contiguous (a cut or a reorder) — this is what makes live Play follow
   *  the EDITED beat sequence instead of the raw master clip. */
  private playSegIndex = 0;
  /** Optional full-bleed blurred-fill background copy of the footage (editor
   *  preview). Muted, plays the same src, mirrored on seek/play/pause so the
   *  letterbox bars behind the contained foreground show soft blurred footage. */
  private _blurVideo: HTMLVideoElement | null = null;
  /** When true, play() sets up the loop but does NOT self-schedule via rAF;
   *  tests drive it deterministically via {@link stepPlaybackForTests}. */
  private manualStepping = false;
  private clock: PlaybackClock = () =>
    typeof performance !== "undefined" ? performance.now() : Date.now();

  /** One-shot SFX playback (music bed stays on the `<audio>` stem). */
  private sfxScheduler: SfxScheduler;
  /** Placed one-shot SFX projected onto the EDITED timeline (edit-tick fire moments). */
  private sfxFires: { editTick: number; assetId: string; volumeMilli: number }[] = [];
  /** Highest edit tick already fired-through; a play/seek resets it so we never
   *  double-fire or blast every earlier sound on a scrub. */
  private lastSfxEditTick = 0;

  constructor(
    private readonly _video: HTMLVideoElement,
    private readonly _audio: HTMLAudioElement,
    private readonly _mount: HTMLElement,
    private pack: SourcePack,
    private readonly variant: RenderVariant,
    private readonly onTick: (t: number) => void,
    private readonly onDispose: () => void,
    initialResolved: ResolvedEdit,
  ) {
    // Binding-based media mapping (H1): source ranges come from the hybrid
    // variant's beatBindings, not the legacy semantic pack.beats ranges.
    this.timeline = buildHybridTimeline(this.pack, initialResolved, this.variant);
    // Set the initial stem src based on the resolved sound choice
    this._audio.src = stemSrc(resolveActiveStemAssetId(initialResolved, this.pack), this.pack);
    this.sfxScheduler = new SfxScheduler((assetId) => {
      const a = this.pack.assets.find((x) => x.id === assetId && x.kind === "audio");
      return a ? { path: a.path, fallbackPath: a.fallbackPath } : null;
    });
    this.recomputeSfxFires(initialResolved);
  }
  get currentTick(): number { return this.tick; }
  /** The host element that was passed to createPreview — a stable HTMLElement handle. */
  get videoHost(): HTMLElement { return this._mount; }
  /** The underlying video element for canvas compositing. */
  get video(): HTMLVideoElement { return this._video; }
  /** The current resolved hybrid timeline (updated by updateTimeline). */
  get currentTimeline(): HybridTimeline { return this.timeline; }
  updateTimeline(graph: EditGraph, resolved: ResolvedEdit): void {
    void graph; // graph is part of the interface contract but not needed by buildHybridTimeline
    this.timeline = buildHybridTimeline(this.pack, resolved, this.variant);
    this._audio.src = stemSrc(resolveActiveStemAssetId(resolved, this.pack), this.pack);
    this.recomputeSfxFires(resolved);
  }

  /** Map a SOURCE-footage tick to the EDITED-timeline tick where it is shown, or
   *  null when that moment was cut/trimmed out of the reel. */
  private sourceTickToEditTick(srcTick: number): number | null {
    for (const seg of this.timeline.segments) {
      if (srcTick >= seg.srcStartTick && srcTick < seg.srcEndTick) {
        return seg.editStartTick + (srcTick - seg.srcStartTick);
      }
    }
    return null;
  }

  /** Recompute the edit-tick fire moments for the placed SFX + prefetch buffers. */
  private recomputeSfxFires(resolved: ResolvedEdit): void {
    const fires: { editTick: number; assetId: string; volumeMilli: number }[] = [];
    for (const inst of resolved.sfx) {
      const editTick = this.sourceTickToEditTick(inst.atTick);
      if (editTick === null) continue; // its moment isn't in the edited reel
      fires.push({ editTick, assetId: inst.assetId, volumeMilli: inst.volumeMilli });
    }
    fires.sort((a, b) => a.editTick - b.editTick);
    this.sfxFires = fires;
    this.sfxScheduler.prefetch(resolved.sfx.map((s) => s.assetId));
  }

  /** Fire every placed SFX whose moment falls in (lastSfxEditTick, tick]. */
  private fireSfxUpTo(tick: number): void {
    if (tick < this.lastSfxEditTick) {
      this.lastSfxEditTick = tick;
      return;
    }
    for (const f of this.sfxFires) {
      if (f.editTick > this.lastSfxEditTick && f.editTick <= tick) {
        this.sfxScheduler.fire(f.assetId, f.volumeMilli);
      }
    }
    this.lastSfxEditTick = tick;
  }

  /** onTick during playback: notify the shell AND fire any crossed one-shots. */
  private notifyPlaybackTick(tick: number): void {
    this.onTick(tick);
    this.fireSfxUpTo(tick);
  }

  /** Attach the full-bleed blurred-fill background video (editor preview). It is
   *  owned by the session lifecycle: mirrored on seek/play/pause and removed on
   *  dispose so no stray element survives teardown. */
  attachBlurFillVideo(video: HTMLVideoElement | null): void {
    this._blurVideo = video;
  }

  private mirrorBlurTime(sec: number): void {
    if (!this._blurVideo) return;
    try {
      this._blurVideo.currentTime = sec;
    } catch {
      /* ignore */
    }
  }

  /** Test seam: enable manual, rAF-free stepping BEFORE calling play(). */
  setManualSteppingForTests(on: boolean): void {
    this.manualStepping = on;
  }
  /** Test seam: swap the one-shot SFX scheduler for a recording double. */
  setSfxSchedulerForTests(scheduler: Pick<SfxScheduler, "prefetch" | "fire" | "dispose">): void {
    this.sfxScheduler = scheduler as SfxScheduler;
  }
  /** Test seam: inject a deterministic clock for the rAF loop's dt. */
  setClockForTests(clock: PlaybackClock | null): void {
    this.clock =
      clock ?? (() => (typeof performance !== "undefined" ? performance.now() : Date.now()));
  }
  /**
   * Test seam: advance the play loop by `deltaTicks` edited ticks (default 1),
   * running exactly one step without real rAF/timers. No-op unless playing.
   */
  stepPlaybackForTests(deltaTicks = 1): void {
    if (!this.playing || this.disposed) return;
    this.advanceBy(deltaTicks);
  }

  /** Index of the kept-beat segment containing `editTick` (last segment if past end). */
  private segmentIndexForEditTick(editTick: number): number {
    const segs = this.timeline.segments;
    for (let i = 0; i < segs.length; i++) {
      const s = segs[i]!;
      if (editTick >= s.editStartTick && editTick < s.editEndTick) return i;
    }
    return segs.length > 0 ? segs.length - 1 : 0;
  }

  /** Seek video (muted, scrubbed) + audio stem to the current edit tick. */
  private applyPlayhead(): void {
    if (this.disposed) return;
    const videoTimeSec = hybridEditTickToVideoTimeSec(this.timeline, this.tick, this.pack.tickRate);
    this._video.currentTime = videoTimeSec;
    this.mirrorBlurTime(videoTimeSec);
    // The stem is scored against the EDITED timeline, so its position is edit time.
    this._audio.currentTime = this.tick / this.pack.tickRate;
  }

  private playAudio(): void {
    try {
      const p = this._audio.play?.();
      if (p && typeof (p as Promise<void>).then === "function") {
        (p as Promise<void>).catch(() => undefined);
      }
    } catch {
      /* happy-dom / no-src: ignore */
    }
  }

  private finishPlay(): void {
    const resolve = this.playResolve;
    this.playResolve = null;
    resolve?.();
  }

  /** Detach the native continuous-playback listeners (timeupdate/ended). */
  private detachNativePlaybackListeners(): void {
    if (this.timeUpdateHandler) {
      this._video.removeEventListener?.("timeupdate", this.timeUpdateHandler);
      this.timeUpdateHandler = null;
    }
    if (this.endedHandler) {
      this._video.removeEventListener?.("ended", this.endedHandler);
      this.endedHandler = null;
    }
  }

  /** Stop the loop, pause the stem + video, and resolve the pending play() promise. */
  private stopPlayback(): void {
    this.playing = false;
    this.detachNativePlaybackListeners();
    this._audio.pause?.();
    this._video.pause?.();
    this._blurVideo?.pause?.();
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
    this.finishPlay();
  }

  /**
   * H2/play2 fix — real-time continuous playback that follows the EDITED beat
   * sequence (browser only; the deterministic test seam uses manual stepping).
   *
   * The muted `<video>` runs natively via `play()` for smoothness WITHIN a kept
   * beat (no per-frame seeks → no audio stutter / frame jumping). At each BEAT
   * BOUNDARY the `timeupdate` handler advances to the NEXT kept beat in edit
   * order and, when that beat's source start is not contiguous with the beat that
   * just ended (a CUT or a REORDER), SEEKS the element there before continuing.
   * This is what makes live Play skip cut beats and honour reorders, matching
   * export. The scrubber tick is driven from the position within the current kept
   * beat, projected onto the EDITED timeline. The audio stem is a continuous mood
   * bed scored in edit time — it keeps playing and is never re-seeked here.
   */
  private startNativePlayback(): void {
    const segs = this.timeline.segments;
    if (segs.length === 0) {
      this.stopPlayback();
      return;
    }
    // Begin inside whichever kept beat the current edit tick sits in. applyPlayhead()
    // (called by play() before this) already aligned the <video> to its source start.
    this.playSegIndex = this.segmentIndexForEditTick(this.tick);
    const rate = this.pack.tickRate;
    const eps = 1e-3;

    // Let the muted element play in real time (audio comes from the stem, which
    // playAudio() already started). The <video> stays muted per contract.
    const startNative = (el: HTMLVideoElement | null): void => {
      if (!el) return;
      try {
        const p = el.play?.();
        if (p && typeof (p as Promise<void>).then === "function") {
          (p as Promise<void>).catch(() => undefined);
        }
      } catch {
        /* happy-dom / autoplay block: ignore — timeupdate simply won't fire */
      }
    };
    startNative(this._video);
    // The blurred-fill copy rides along (heavily blurred → exact sync unneeded).
    startNative(this._blurVideo);

    const onTimeUpdate = (): void => {
      if (!this.playing || this.disposed) return;
      // Advance across every beat boundary that the element has passed since the
      // last update (usually zero or one; a loop guards very short beats).
      while (this.playSegIndex < segs.length) {
        const seg = segs[this.playSegIndex]!;
        const srcEndSec = seg.srcEndTick / rate;
        const t = this._video.currentTime ?? 0;
        if (t < srcEndSec - eps) break; // still inside this kept beat

        const nextIdx = this.playSegIndex + 1;
        if (nextIdx >= segs.length) {
          // Last kept beat finished — land on the final edit tick and stop.
          this.tick = Math.max(0, this.timeline.durationTicks - 1);
          this.notifyPlaybackTick(this.tick);
          this.stopPlayback();
          return;
        }
        const nextSeg = segs[nextIdx]!;
        const nextStartSec = nextSeg.srcStartTick / rate;
        // Seek ONLY at the boundary and ONLY when the next kept beat's source is
        // not contiguous (a cut or a reorder). A contiguous run keeps playing so
        // audio/video stay smooth.
        const discontiguous = Math.abs(nextStartSec - srcEndSec) > eps;
        if (discontiguous) {
          this._video.currentTime = nextStartSec;
          this.mirrorBlurTime(nextStartSec);
        }
        this.playSegIndex = nextIdx;
        this.tick = nextSeg.editStartTick;
        this.notifyPlaybackTick(this.tick);
        // After a real seek the element now sits at nextStart (inside the new
        // beat) — stop advancing this update. A contiguous advance re-checks.
        if (discontiguous) return;
      }

      // Within the current kept beat: project source time onto THIS segment's edit
      // range so the scrubber represents edited time (matches what export emits).
      const seg = segs[Math.min(this.playSegIndex, segs.length - 1)]!;
      const videoTick = (this._video.currentTime ?? 0) * rate;
      const srcLen = seg.srcEndTick - seg.srcStartTick;
      const frac = srcLen > 0 ? Math.max(0, Math.min(1, (videoTick - seg.srcStartTick) / srcLen)) : 0;
      const editTick = Math.round(seg.editStartTick + frac * (seg.editEndTick - seg.editStartTick));
      if (editTick !== this.tick) {
        this.tick = editTick;
        this.notifyPlaybackTick(this.tick);
      }
    };
    const onEnded = (): void => {
      if (!this.playing || this.disposed) return;
      this.tick = Math.max(0, this.timeline.durationTicks - 1);
      this.notifyPlaybackTick(this.tick);
      this.stopPlayback();
    };

    this.timeUpdateHandler = onTimeUpdate;
    this.endedHandler = onEnded;
    this._video.addEventListener?.("timeupdate", onTimeUpdate);
    this._video.addEventListener?.("ended", onEnded);
  }

  /** One playback step: advance the edit clock, remap media, fire the UI tick. */
  private advanceBy(deltaTicks: number): void {
    if (!this.playing || this.disposed) return;
    const dur = this.timeline.durationTicks;
    if (dur <= 0) {
      this.stopPlayback();
      return;
    }
    this.tick += Math.max(0, deltaTicks);
    if (this.tick >= dur) {
      // Land exactly on the last edited tick, apply, notify, then resolve.
      this.tick = Math.max(0, dur - 1);
      this.applyPlayhead();
      this.notifyPlaybackTick(this.tick);
      this.stopPlayback();
      return;
    }
    this.applyPlayhead();
    this.notifyPlaybackTick(this.tick);
  }

  async seek(tick: number): Promise<void> {
    // Claim a new epoch token. Any concurrently-awaiting seek will see the
    // epoch change and resolve as "superseded" without committing its tick.
    const epoch = ++this._seekEpoch;
    const videoTimeSec = hybridEditTickToVideoTimeSec(this.timeline, tick, this.pack.tickRate);
    // Thread seekDeadbandSec from the variant config (default 0.08 s).
    const cfg = this.variant.config as Record<string, unknown>;
    const deadband = typeof cfg?.seekDeadbandSec === "number" ? cfg.seekDeadbandSec : 0.08;
    const result = await seekVideoWithGate(this._video, videoTimeSec, {
      deadbandSec: deadband,
      supersededSignal: () => this._seekEpoch !== epoch,
    });
    // Superseded: a later seek is in flight — do NOT commit a stale tick.
    if (result.status === "superseded") return;
    // MF4: only advance currentTick and fire onTick when the gate confirmed a
    // real decoded frame landed within the deadband.  On timed-out or
    // out-of-tolerance the preview stays at the last committed tick — stale
    // frames must never be accepted as the new edit position.
    if (result.status !== "committed") return;
    this.tick = tick;
    // Scrubbing repositions the baseline WITHOUT firing — no sound blast on seek.
    this.lastSfxEditTick = tick;
    this._audio.currentTime = this.tick / this.pack.tickRate;
    this.mirrorBlurTime(videoTimeSec);
    this.onTick(tick);
  }

  /**
   * Play the EDITED timeline (not the raw master clip). Advances an edit-tick
   * clock at `pack.tickRate`; each step remaps to the H1 binding video time and
   * keeps the stem in sync. Resolves when the edited duration is reached, or
   * when pause()/dispose() stops the loop — mirroring the 3D session's contract
   * the shell relies on (`await session.play(); setPlaying(false)`).
   */
  async play(): Promise<void> {
    if (this.playing || this.disposed) return;
    const dur = this.timeline.durationTicks;
    if (dur <= 0) return;
    const lastTick = dur - 1;
    // Restart from the top when parked at/after the end.
    if (this.tick >= lastTick) {
      this.tick = 0;
      this.applyPlayhead();
      this.onTick(0);
    }
    this.playing = true;
    // Baseline for one-shot firing: sounds strictly after the start point fire
    // as we pass them (never a burst of everything before the playhead).
    this.lastSfxEditTick = this.tick;
    this.playAudio();
    this.applyPlayhead();
    this.lastWall = this.clock();

    await new Promise<void>((resolve) => {
      this.playResolve = resolve;
      // Manual test mode: caller drives via stepPlaybackForTests(); no native play.
      if (this.manualStepping) return;
      // Real browser path (H2 fix): native continuous playback + timeupdate tick.
      // Replaces the old rAF seek-per-frame loop that caused choppy audio and
      // frame-jumping (it never called the native <video>.play()).
      this.startNativePlayback();
    });
  }

  async pause(): Promise<void> {
    this.playing = false;
    this.detachNativePlaybackListeners();
    this._audio.pause?.();
    this._video.pause?.();
    this._blurVideo?.pause?.();
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
    this.finishPlay();
    this.onTick(this.tick);
  }

  dispose(): void {
    this.disposed = true;
    this.playing = false;
    this.detachNativePlaybackListeners();
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
    this._audio.pause?.();
    this._blurVideo?.pause?.();
    this.sfxScheduler.dispose();
    this.finishPlay();
    this._video.remove();
    this._audio.remove();
    this._blurVideo?.remove();
    this._blurVideo = null;
    this.onDispose();
  }
}
